/**
 * ENDESGA 32 (by ENDESGA, lospec.com/palette-list/endesga-32), one mnemonic character per colour, plus one skin-only
 * tone (`a`, ADR-0088). Every pixel of every sprite is one of these. `.` means transparent.
 *
 * Use ramps (dark → light) for shading; never jump more than one step between neighbouring pixels of a
 * surface except for deliberate highlights.
 */
export const PALETTE = {
  // cool greys / outline ramp
  k: '#181425', // near-black: outlines, deepest shadow
  '1': '#262b44', // navy black
  '2': '#3a4466', // dark slate
  '3': '#5a6988', // slate
  '4': '#8b9bb4', // grey-blue
  '5': '#c0cbdc', // light grey
  w: '#ffffff', // white: sparkles, highlights, flashes
  // browns / wood / straw
  N: '#3e2731', // deep brown-plum
  n: '#733e39', // brown
  m: '#b86f50', // mid brown (wood light)
  t: '#e4a672', // tan
  e: '#ead4aa', // ecru / cream / straw light
  // skin
  S: '#c28569', // skin shadow
  s: '#e8b796', // skin light
  a: '#94523a', // umber: a warm dark skin between m and n (skin only, ADR-0088; not ENDESGA's)
  // reds / warm
  R: '#a22633', // dark red
  r: '#e43b44', // red
  x: '#ff0044', // hot red: damage, alerts
  u: '#be4a2f', // rust
  U: '#d77643', // burnt orange
  o: '#f77622', // orange
  Y: '#feae34', // gold
  y: '#fee761', // yellow
  // greens
  D: '#193c3e', // deepest teal-green
  G: '#265c42', // dark green
  g: '#3e8948', // green
  l: '#63c74d', // light green
  // blues
  B: '#124e89', // dark blue
  b: '#0099db', // blue
  c: '#2ce8f5', // cyan
  // purples / pinks
  P: '#68386c', // purple
  p: '#b55088', // magenta
  i: '#f6757a', // pink
} as const;

export type PaletteKey = keyof typeof PALETTE;
export const PALETTE_KEYS = Object.keys(PALETTE) as PaletteKey[];

export const isPaletteKey = (ch: string): ch is PaletteKey => Object.hasOwn(PALETTE, ch);

/** Shading ramps, darkest first. Pick a ramp per material and stay on it. */
export const RAMPS = {
  outline: ['k', '1', '2'],
  stone: ['k', '1', '2', '3', '4', '5', 'w'],
  foliage: ['D', 'G', 'g', 'l', 'y'],
  wood: ['N', 'n', 'm', 't', 'e'],
  straw: ['n', 'U', 'Y', 'y', 'e'],
  skin: ['n', 'S', 's'],
  red: ['N', 'R', 'r', 'i'],
  fire: ['u', 'o', 'Y', 'y', 'w'],
  water: ['1', 'B', 'b', 'c', 'w'],
  magic: ['k', 'P', 'p', 'i'],
} as const satisfies Record<string, readonly PaletteKey[]>;

export function hexToRgba(hex: string): [number, number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
}
